import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import type { JcDisplayGroup, JcDisplayGroupKind } from "@/types/jcFilterSystem"

const httpService = new HttpService()

interface ListResponse<T> {
  data: { total: number; data: T[] }
}

export interface DisplayGroupCreatePayload {
  id?: string
  technicalName: string
  kind: JcDisplayGroupKind
  required?: boolean
  parentId?: string | null
  sizeSystemKey?: string | null
  position?: number
  setRelevant?: boolean
  translations: Record<string, { name: string }>
}

export type DisplayGroupUpdatePayload = Partial<DisplayGroupCreatePayload>

interface JcDisplayGroupsState {
  groups: JcDisplayGroup[]
  loading: boolean
  loaded: boolean
}

export const useJcDisplayGroupsStore = defineStore({
  id: "jc.displayGroups",
  state: (): JcDisplayGroupsState => ({
    groups: [],
    loading: false,
    loaded: false,
  }),
  getters: {
    // Catalog entries split by kind — handy for the product editor (garments)
    // and for the parent picker (groups).
    garments(): JcDisplayGroup[] {
      return this.groups.filter((g) => g.kind === "garment")
    },
    parentGroups(): JcDisplayGroup[] {
      return this.groups.filter((g) => g.kind === "group")
    },
    // technicalNames of all garment entries — used to pull a product's garment
    // key out of its mixed displayGroups list.
    garmentKeys(): string[] {
      return this.garments.map((g) => g.technicalName)
    },
  },
  actions: {
    async fetchGroups(force = false): Promise<boolean> {
      if (this.loaded && !force) return true
      this.loading = true
      try {
        const result = (await httpService.get(CONSTANT.API.JC_FILTER.DISPLAY_GROUP.LIST)) as ListResponse<JcDisplayGroup>
        this.groups = result?.data?.data ?? []
        this.loaded = true
        return true
      } catch (error) {
        console.error("Failed to fetch display groups", error)
        this.groups = []
        return false
      } finally {
        this.loading = false
      }
    },

    async createGroup(payload: DisplayGroupCreatePayload): Promise<string | null> {
      try {
        const result = (await httpService.post(CONSTANT.API.JC_FILTER.DISPLAY_GROUP.CREATE, payload)) as { data: { id: string } }
        await this.fetchGroups(true)
        return result?.data?.id ?? null
      } catch (error) {
        console.error("Failed to create display group", error)
        return null
      }
    },

    async updateGroup(id: string, payload: DisplayGroupUpdatePayload): Promise<boolean> {
      try {
        await httpService.patch(CONSTANT.API.JC_FILTER.DISPLAY_GROUP.UPDATE.replace("{id}", id), payload)
        await this.fetchGroups(true)
        return true
      } catch (error) {
        console.error("Failed to update display group", error)
        return false
      }
    },

    async deleteGroup(id: string): Promise<boolean> {
      try {
        await httpService.delete(CONSTANT.API.JC_FILTER.DISPLAY_GROUP.DELETE.replace("{id}", id))
        await this.fetchGroups(true)
        return true
      } catch (error) {
        console.error("Failed to delete display group", error)
        return false
      }
    },
  },
})
